import React, { useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import bgImage from '../assets/images/new_bgs.png';
import logoImage from '../assets/images/payPlus/payPlusNoBackground.png';
import logoImageWithTitle from '../assets/images/payPlus/payPlusWithTitle.png';
import axiosInstance from '../utils/axiosInstance';
import InputText from '../components/InputText';

const NewPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = Yup.object({
    identityNumber: Yup.string()
      .matches(/^[0-9]{11}$/, t('identityNumberFormat'))
      .required(t('identityNumberRequired'))
  });

  const formik = useFormik({
    initialValues: {
      identityNumber: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.post(`/api/v1/no-auth/new-password-request/${values.identityNumber}`);

        if (response.status === 200) {
          toast.current.show({
            severity: 'success',
            summary: t('success'),
            detail: t('passwordResetLinkSent'),
            life: 5000
          });

          // 3 saniye sonra login sayfasına yönlendir
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        }
      } catch (error) {
        console.error('Password reset error:', error);

        let errorMessage = t('passwordResetError');

        if (error.response?.status === 404) {
          errorMessage = t('identityNumberNotFound');
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        toast.current.show({
          severity: 'error',
          summary: t('error'),
          detail: errorMessage,
          life: 5000
        });
      } finally {
        setIsLoading(false);
      }
    }
  });

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <Toast ref={toast} />

      <div
        className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 dark:filter dark:grayscale-[70%]"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />

      <div className="relative flex gap-0 md:gap-10 p-5 md:p-10 h-full">
        {/* Left Section - Logo and Image - sadece büyük ekranlarda görünür */}
        <div className="hidden md:flex h-[90vh] w-[50vw] bg-white bg-opacity-50 justify-center items-center rounded-3xl">
          <div className="h-[90%] w-[90%] flex justify-start items-center bg-white bg-opacity-80 p-2 rounded-3xl relative shadow-2xl shadow-gray-500">
            <img
              className='dark:filter dark:grayscale-[70%]'
              src={logoImageWithTitle}
              alt="Logo"
            />
          </div>
        </div>

        {/* Right Section - Password Reset Form */}
        <div className="h-[90vh] w-full md:w-[50vw] bg-white bg-opacity-50 p-3 md:p-5 flex justify-center items-center rounded-3xl">
          <div className="bg-white bg-opacity-90 rounded-3xl p-2 shadow-2xl shadow-gray-500 h-fit w-full md:w-[90%] max-w-md">
            <form onSubmit={formik.handleSubmit} className="flex flex-col">
              <div className="flex justify-center py-4">
                <img
                  src={logoImage}
                  alt="Logo"
                  className="h-[8vh] md:h-[10vh] w-[12vw] md:w-[8vw] object-contain dark:filter dark:grayscale-[70%]"
                />
              </div>

              <div className="text-center flex flex-col items-center gap-y-2 py-4">
                <h1 className="font-firaSans font-bold text-xl md:text-2xl text-logo-darkest">
                  {t('renewPassword')}
                </h1>
                <p className="text-xs md:text-sm text-logo-dark text-center px-4">
                  {t('enterIdentityNumberForReset')}
                </p>
              </div>

              <div className="flex flex-col gap-y-4 p-4 md:p-6">
                <InputText
                  label={t('identityNumber')}
                  name="identityNumber"
                  value={formik.values.identityNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  clearFunction={() => formik.setFieldValue("identityNumber", "")}
                  error={formik.errors.identityNumber}
                  touched={formik.touched.identityNumber}
                  required
                  maxLength="11"
                  placeholder="12345678901"
                />

                <Button
                  type="submit"
                  label={t('sendResetLink')}
                  loading={isLoading}
                  className="bg-logo-mid hover:bg-logo-dark dark:bg-gray-500 dark:hover:bg-gray-600 py-4 md:py-5 text-white font-medium rounded-lg shadow-lg mt-4"
                />

                <div className="flex justify-center mt-4">
                  <p
                    onClick={() => navigate('/login')}
                    className="text-center text-logo-dark font-semibold hover:underline hover:text-logo-darkest text-sm md:text-base cursor-pointer"
                  >
                    {t('backToLogin')}
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPassword;